import React, { useState, useEffect } from 'react';
import { eventLivePillsFromCounts } from '../lib/features';

// Tiny count-up animation, 0 → target in ~1.2s. Single rAF loop, no
// setInterval — matches the homepage pill timing for a coherent feel.
const useCountUp = (target, durMs = 1200) => {
  const [n, setN] = useState(0);
  useEffect(() => {
    if (target == null) return undefined;
    let raf;
    const start = performance.now();
    const step = (t) => {
      const k = Math.min(1, (t - start) / durMs);
      const eased = 1 - Math.pow(1 - k, 3);
      setN(Math.round(eased * target));
      if (k < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [target, durMs]);
  return n;
};

const Pill = ({ emoji, label, value, accent }) => {
  const n = useCountUp(value);
  return (
    <div
      data-testid={`event-live-pill-${label.split(' ')[0]}`}
      style={{
        height: 40, padding: '0 14px',
        display: 'inline-flex', alignItems: 'center', gap: 8,
        background: 'rgba(255,255,255,0.04)',
        border: `1px solid ${accent}33`,
        borderRadius: 999,
        boxShadow: `0 0 20px ${accent}22, 0 1px 0 rgba(255,255,255,0.04) inset`,
        backdropFilter: 'blur(10px)',
        whiteSpace: 'nowrap',
      }}
    >
      <span style={{ fontSize: 16, lineHeight: 1, filter: `drop-shadow(0 0 5px ${accent}66)` }}>{emoji}</span>
      <span style={{
        fontSize: 14, fontWeight: 800, color: '#FFFFFF',
        letterSpacing: '-0.01em', fontVariantNumeric: 'tabular-nums',
      }}>{n.toLocaleString()}</span>
      <span style={{ fontSize: 12, color: '#B8B8C2', fontWeight: 600 }}>{label}</span>
    </div>
  );
};

// Renders the per-event live counter pills strip. Hides if no metric
// is above the min-display threshold (handled inside
// eventLivePillsFromCounts). Per the brief: lives under the event
// title, above the action buttons.
export const EventLivePills = ({ counts, testId = 'event-live-pills' }) => {
  const pills = eventLivePillsFromCounts(counts);
  if (pills.length === 0) return null;
  return (
    <div
      data-testid={testId}
      className="flex flex-wrap items-center gap-2 mb-5"
      style={{ justifyContent: 'flex-start' }}
    >
      {pills.map((p) => <Pill key={p.key} {...p} />)}
    </div>
  );
};

export default EventLivePills;
